/**
 * Avatar placeholder utilities - use data URIs to avoid external dependencies
 * (via.placeholder.com can fail with ERR_NAME_NOT_RESOLVED when blocked or unreachable)
 */

/**
 * Returns a data URI SVG for an avatar placeholder with the given initial.
 * @param {string} letter - Single character to display (e.g. 'U', 'T')
 * @param {number} size - Size in pixels (default 40)
 * @param {string} bgColor - Hex background color (default 667eea)
 */
export function getAvatarPlaceholder(letter = 'U', size = 40, bgColor = '667eea') {
  const safeLetter = String(letter || 'U').charAt(0).toUpperCase();
  const enc = encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><rect fill="#${bgColor}" width="${size}" height="${size}"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="white" font-size="${size * 0.5}" font-family="sans-serif">${safeLetter}</text></svg>`
  );
  return `data:image/svg+xml,${enc}`;
}

/**
 * Returns a safe avatar URL - validates backend response and falls back to placeholder.
 * Handles: null/undefined, relative paths (prepends baseUrl), invalid/corrupted values.
 * @param {string|null|undefined} avatar - Avatar URL from backend
 * @param {string} fallbackLetter - Letter for placeholder (e.g. 'U', 'T')
 * @param {string} baseUrl - API base URL for relative paths (e.g. '')
 * @param {number} size - Size for placeholder (default 40)
 */
export function getSafeAvatarUrl(avatar, fallbackLetter = 'U', baseUrl = '', size = 40) {
  if (!avatar || typeof avatar !== 'string') {
    return getAvatarPlaceholder(fallbackLetter, size);
  }
  const trimmed = avatar.trim();
  if (!trimmed) return getAvatarPlaceholder(fallbackLetter, size);
  // Valid absolute URLs
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:')) {
    return trimmed;
  }
  // Relative path - prepend base
  if (trimmed.startsWith('/')) {
    return `${baseUrl}${trimmed}`;
  }
  // Corrupted/invalid (e.g. "ffffff?text=U") - use placeholder
  return getAvatarPlaceholder(fallbackLetter, size);
}
